import React from 'react';
import { Modal, Radio } from 'antd';
import CodeEditorSheet, { codeModalProps } from '@src/components/Common/CodeEditorSheet';
import useCaseEditor, { CaseDraft } from './useCaseEditor';
import { EvaluationCase } from './types';

/**
 * One evaluation case, written the way every other piece of JSON in this app is
 * written: the shared code sheet, with the one error the content can have on
 * its chrome rather than under a 50vh editor.
 *
 * Rendered only while it is open — the caller mounts it on a draft and drops it
 * on close — so a form that is not adding a case carries no modal at all.
 */
const EvaluationCaseModal = ({
    cases,
    draft,
    onCommit,
    onCancel,
}: {
    cases: EvaluationCase[];
    draft: CaseDraft;
    onCommit: (next: EvaluationCase, index: number | null) => void;
    onCancel: () => void;
}) => {
    const { text, setText, isValid, setIsValid, error, commit } = useCaseEditor({ cases, draft, onCommit });
    const editing = draft.index !== null;

    return (
        <Modal
            {...codeModalProps(editing ? `Test case ${draft.index! + 1}` : 'New test case')}
            open
            onOk={commit}
            onCancel={onCancel}
            okText={editing ? 'Save case' : 'Add case'}
        >
            <CodeEditorSheet
                language="json"
                value={text}
                onChange={setText}
                error={error}
                hint="The consumer declaration the validator is asked to judge"
                ariaLabel="Case declaration"
            />

            {/* The verdict is the whole point of the case, so it gets the room
                a choice needs: two tiles wearing the same chips the case list
                and the run's rows wear, each saying what picking it means. */}
            <fieldset className="aip-pick">
                <legend className="aip-pick__legend">This declaration should be</legend>
                <Radio.Group
                    className="aip-pick__group"
                    value={isValid}
                    onChange={(event) => setIsValid(event.target.value)}
                >
                    <Radio className="aip-pick__opt" value>
                        <span className="aip-verdict aip-verdict--yes">Approved</span>
                        <span className="aip-pick__note">The validator lets the change through</span>
                    </Radio>
                    <Radio className="aip-pick__opt" value={false}>
                        <span className="aip-verdict aip-verdict--no">Rejected</span>
                        <span className="aip-pick__note">The validator turns the change down</span>
                    </Radio>
                </Radio.Group>
            </fieldset>
        </Modal>
    );
};

export default EvaluationCaseModal;
